import { Server, UserCog, FileCheck2, Lock } from "lucide-react";
import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { ScreenBlock, CTABand, SectionHead, Pill } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { CQCScreen } from "@/components/site/mockups";

const EVIDENCE = ["Training compliance", "Supervisions", "Incidents", "Safeguarding", "Complaints", "Audits", "Action plans", "Governance evidence", "CQC readiness indicators"];

const SECURITY = [
  { icon: Server, title: "Secure organisation separation", desc: "Each organisation's data is kept separate within a secure multi-tenant architecture." },
  { icon: UserCog, title: "Role-based access", desc: "Admin and manager permissions control access to sensitive care information." },
  { icon: FileCheck2, title: "Audit trails", desc: "Actions are recorded to support accountability and governance." },
  { icon: Lock, title: "Secure authentication", desc: "Controlled, authenticated access across multi-site organisations." },
];

export default function CQCGovernance() {
  useSEO(
    "CQC & Governance Software | AYOBELL Care Governance",
    "AYOBELL brings governance evidence together — incidents, safeguarding, supervisions, audits and AYOBELL readiness indicators — to support inspection preparation and continuous improvement."
  );
  return (
    <>
      <PageHero
        eyebrow="CQC & Governance"
        title="Be ready for the questions that matter."
        subtitle="AYOBELL helps providers bring operational and governance evidence together — ready when you need it."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
        screenKey="cqc"
        screenComp={CQCScreen}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="Readiness dashboard"
            title="The AYOBELL CQC Readiness Dashboard"
            desc="A single view that draws together governance evidence and AYOBELL readiness indicators to support inspection preparation and continuous improvement."
            points={["Training compliance & supervisions", "Incidents, safeguarding & complaints", "Audits & action plans", "AYOBELL readiness indicators"]}
            screenKey="cqc"
            url="app.ayobell.com/governance"
          />

          <Reveal className="mt-12 flex flex-wrap gap-2">
            {EVIDENCE.map((e) => <Pill key={e}>{e}</Pill>)}
          </Reveal>

          <Reveal className="mt-10">
            <div className="rounded-2xl border border-teal-200 bg-teal-50/50 p-6 max-w-3xl">
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Important:</span> AYOBELL's readiness indicators are internal management tools designed to support inspection preparation and continuous improvement. They do not constitute an official CQC rating or guarantee regulatory compliance.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 ayo-grid-bg-dark opacity-40" />
        <div className="absolute -top-20 right-0 w-[30rem] h-[30rem] rounded-full bg-teal-500/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full">Security</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-4">Security designed around sensitive care information.</h2>
            <p className="mt-3 text-slate-300">Foundations that keep care information protected and access controlled.</p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {SECURITY.map((s) => (
              <StaggerItem key={s.title}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-teal-500/15 text-teal-300"><s.icon className="w-5 h-5" /></span>
                  <h4 className="font-semibold text-base mt-4">{s.title}</h4>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand title="See how AYOBELL supports your governance." />
    </>
  );
}
